import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search as SearchIcon, Loader2 } from 'lucide-react';
import { fetchProducts } from '@/services/productService';
import { Product } from '@/types/product';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/use-debounce';
import { useLanguage } from '@/context/LanguageContext';

const Search: React.FC = () => {
  const { language, tl } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery.trim()) {
        setProducts([]);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        const searchResults = await fetchProducts({ search: debouncedSearchQuery });
        setProducts(searchResults);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to fetch search results. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    performSearch();
  }, [debouncedSearchQuery]);
  
  useEffect(() => {
    if (debouncedSearchQuery) {
      setSearchParams({ q: debouncedSearchQuery });
    } else {
      setSearchParams({});
    }
  }, [debouncedSearchQuery, setSearchParams]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The search is already handled by the useEffect with debounce
  };

  return (
    <>
      <Helmet>
        <title>{tl('Recherche de Produits', 'Search Products', 'Ricerca prodotti')} | Meubles Karim</title>
        <meta name="description" content={tl('Recherchez dans notre collection de meubles premium pour votre maison.', 'Search our collection of premium furniture for your home.', 'Cercate nella nostra collezione di mobili premium per la vostra casa.')} />
      </Helmet>
      
      <main className="bg-white text-black">
        <section className="pb-8 pt-[88px] md:pb-16 lg:pt-[104px]">
          <div className="container mx-auto px-4">
            <div className="mb-12">
              <h1 className="text-2xl md:text-4xl text-black font-sans font-semibold mb-4 text-center">
                {tl('Recherche de Produits', 'Search Our Collection', 'Ricerca prodotti')}
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto text-center">
                {tl(
                  'Trouvez le meuble parfait pour votre intérieur',
                  'Find the perfect furniture for your home',
                  'Trovate il mobile perfetto per il vostro interno'
                )}
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto mb-12">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <div className="relative flex-grow">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="search"
                    placeholder={tl(
                      'Rechercher par nom, catégorie ou matériau...',
                      'Search for furniture by name, category, or material...',
                      'Cerca per nome, categoria o materiale...'
                    )}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white text-black border-gray-300"
                  />
                </div>
                <Button type="submit" variant="luxury">
                  {tl('Rechercher', 'Search', 'Cerca')}
                </Button>
              </form>
            </div>
            
            {isLoading && (
              <div className="text-center py-12">
                <Loader2 className="animate-spin h-8 w-8 mx-auto text-furniture-brown" />
                <p className="mt-2 text-muted-foreground">
                  {tl('Recherche en cours...', 'Searching for products...', 'Ricerca in corso...')}
                </p>
              </div>
            )}
            
            {error && (
              <div className="max-w-3xl mx-auto mb-8">
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
                  {error}
                </div>
              </div>
            )}
            
            {!isLoading && !error && debouncedSearchQuery && (
              <>
                <div className="mb-8 text-center">
                  <p className="text-muted-foreground">
                    {products.length === 0
                      ? `${tl('Aucun produit trouvé pour', 'No products found for', 'Nessun prodotto trovato per')} "${debouncedSearchQuery}"`
                      : language === 'fr'
                        ? `${products.length} produit${products.length !== 1 ? 's' : ''} trouvé${products.length !== 1 ? 's' : ''} pour "${debouncedSearchQuery}"`
                        : language === 'it'
                          ? `${products.length} prodott${products.length !== 1 ? 'i' : 'o'} trovat${products.length !== 1 ? 'i' : 'o'} per "${debouncedSearchQuery}"`
                          : `Found ${products.length} product${products.length !== 1 ? 's' : ''} for "${debouncedSearchQuery}"`}
                  </p>
                </div>
                
                {products.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {products.map((product) => (
                      <Link
                        key={product.id}
                        to={`/product/${product.id}`}
                        className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative"
                      >
                        <div className="relative aspect-square w-full">
                          <img
                            src={product.image_nobg || product.images?.[0] || '/placeholder.svg'}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 bg-white/30 md:bg-white/50 backdrop-blur-sm">
                            <h4 className="text-sm text-black md:text-lg font-semibold">{product.name}</h4>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {!debouncedSearchQuery && !isLoading && (
              <div className="text-center py-12">
                <div className="mb-4">
                  <SearchIcon className="h-16 w-16 mx-auto text-muted-foreground opacity-30" />
                </div>
                <h3 className="text-lg font-medium mb-2">
                  {tl('Commencez votre recherche', 'Start Searching', 'Iniziate la ricerca')}
                </h3>
                <p className="text-muted-foreground">
                  {tl(
                    'Entrez un mot-clé pour rechercher des produits par nom, catégorie ou matériau.',
                    'Enter a keyword to search for products by name, category or material.',
                    'Inserite una parola chiave per cercare prodotti per nome, categoria o materiale.'
                  )}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default Search;
